import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { API_BASE_URL } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import {
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'

interface Plan {
  id: number
  name: string
  slug: string
  price: number
  trial_days: number
}

export default function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan') || 'beta'
  const { login } = useAuthStore()

  const [plans, setPlans] = useState<Plan[]>([])
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [checkingSlug, setCheckingSlug] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const [formData, setFormData] = useState({
    company_name: '',
    company_slug: '',
    cnpj: '',
    email: '',
    phone: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    plan_slug: selectedPlan,
    accept_terms: false
  })

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  })

  useEffect(() => {
    // Load plans
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || API_BASE_URL
    fetch(`${API_BASE_URL}/signup/plans`)
      .then(res => res.json())
      .then(data => {
        setPlans(data.plans || [])
        const plan = data.plans.find((p: Plan) => p.slug === selectedPlan)
        setCurrentPlan(plan || data.plans[0])
      })
      .catch(err => console.error('Error loading plans:', err))
  }, [selectedPlan])

  useEffect(() => {
    // Check password strength
    const password = formData.admin_password
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    })
  }, [formData.admin_password])

  useEffect(() => {
    // Auto-generate slug from company name
    if (formData.company_name && !formData.company_slug) {
      const slug = formData.company_name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

      setFormData(prev => ({ ...prev, company_slug: slug }))
    }
  }, [formData.company_name])

  useEffect(() => {
    // Check slug availability with debounce
    if (!formData.company_slug || formData.company_slug.length < 3) {
      setSlugAvailable(null)
      return
    }

    const timeoutId = setTimeout(() => {
      setCheckingSlug(true)
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || API_BASE_URL
      fetch(`${API_BASE_URL}/signup/check-slug/${formData.company_slug}`)
        .then(res => res.json())
        .then(data => {
          setSlugAvailable(data.available)
          setCheckingSlug(false)
        })
        .catch(() => {
          setCheckingSlug(false)
        })
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [formData.company_slug])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate
    if (!formData.accept_terms) {
      setError('Você precisa aceitar os termos de uso')
      return
    }

    if (formData.admin_password !== formData.admin_password_confirm) {
      setError('As senhas não coincidem')
      return
    }

    if (!Object.values(passwordStrength).every(v => v)) {
      setError('A senha não atende aos requisitos de segurança')
      return
    }

    if (slugAvailable === false) {
      setError('O identificador da empresa já está em uso')
      return
    }

    setLoading(true)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || API_BASE_URL
      const response = await fetch(`${API_BASE_URL}/signup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          company_slug: formData.company_slug,
          cnpj: formData.cnpj || null,
          email: formData.email,
          phone: formData.phone || null,
          admin_name: formData.admin_name,
          admin_email: formData.admin_email,
          admin_password: formData.admin_password,
          plan_slug: formData.plan_slug
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao criar conta')
      }

      // Save token in both localStorage and Zustand store
      localStorage.setItem('token', data.access_token)

      // Update auth store with login data
      login(data)

      navigate('/onboarding')

    } catch (err: any) {
      setError(err.message || 'Erro ao criar conta. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FinPro</span>
            </div>

            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Criar sua conta FinPro
            </h1>
            <p className="text-gray-600">
              {currentPlan && (
                <>
                  Plano <span className="font-semibold text-blue-600">{currentPlan.name}</span> •
                  R$ {currentPlan.price}/mês • {currentPlan.trial_days} dias grátis
                </>
              )}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                  <XCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Company Info */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Dados da Empresa
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Minha Empresa Ltda"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Identificador (URL) *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="company_slug"
                        value={formData.company_slug}
                        onChange={handleInputChange}
                        required
                        pattern="[a-z0-9-]+"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="minha-empresa"
                      />
                      {checkingSlug && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {!checkingSlug && slugAvailable === true && (
                        <CheckCircleIcon className="absolute right-3 top-2.5 h-5 w-5 text-green-500" />
                      )}
                      {!checkingSlug && slugAvailable === false && (
                        <XCircleIcon className="absolute right-3 top-2.5 h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      finpro.com.br/{formData.company_slug || 'sua-empresa'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email da Empresa *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contato@empresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="(11) 9 9999-9999"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ (opcional)
                    </label>
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                </div>
              </div>

              {/* Admin User */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Seus Dados (Administrador)
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="admin_name"
                      value={formData.admin_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="João Silva"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seu Email *
                    </label>
                    <input
                      type="email"
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="joao@empresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="admin_password"
                        value={formData.admin_password}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    {/* Password Strength */}
                    {formData.admin_password && (
                      <div className="mt-2 space-y-1">
                        {[
                          { key: 'length', label: 'Mínimo 8 caracteres' },
                          { key: 'uppercase', label: 'Letra maiúscula' },
                          { key: 'lowercase', label: 'Letra minúscula' },
                          { key: 'number', label: 'Número' }
                        ].map(({ key, label }) => (
                          <div key={key} className="flex items-center text-xs">
                            {passwordStrength[key as keyof typeof passwordStrength] ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-gray-300 mr-1" />
                            )}
                            <span className={passwordStrength[key as keyof typeof passwordStrength] ? 'text-green-600' : 'text-gray-500'}>
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar Senha *
                    </label>
                    <input
                      type="password"
                      name="admin_password_confirm"
                      value={formData.admin_password_confirm}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="••••••••"
                    />
                    {formData.admin_password_confirm && (
                      <p className={`text-xs mt-1 ${
                        formData.admin_password === formData.admin_password_confirm
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {formData.admin_password === formData.admin_password_confirm
                          ? '✓ Senhas coincidem'
                          : '✗ Senhas não coincidem'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="accept_terms"
                  checked={formData.accept_terms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-sm text-gray-600">
                  Eu aceito os{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    Termos de Uso
                  </a>
                  {' '}e{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    Política de Privacidade
                  </a>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !formData.accept_terms || slugAvailable === false}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-lg transition flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Criando conta...
                  </>
                ) : (
                  `Iniciar ${currentPlan?.trial_days || 90} Dias Grátis`
                )}
              </button>

              <p className="text-center text-sm text-gray-600">
                Já tem uma conta?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Fazer login
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
